import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import api from 'core/util/api';

import { ControlGroup, Field, formConsumer, FormGroup, InputGroup, PasswordInput, RadioGroup, Select } from 'core/form';
import { Callout, Flex, Grid, Heading, Icon, Text, Tooltip } from 'core/components';
import {
  CredentialOptionRenderer,
  CredentialValueRenderer,
  KentikAgentOptionRenderer
} from 'app/views/setup/tasks/nms/optionRenderers';
import SetupCredentials from 'app/views/setup/tasks/nms/SetupCredentials';
import { GROUP_ID_PREFIX, Statuses } from '@kentik/ui-shared/agents/constants';
import MonitoringTemplateSelector from 'app/views/metrics/components/MonitoringTemplateSelect';
import { CREDENTIAL_TYPES } from '@kentik/ui-shared/credentials/constants';
import { isIpValid } from 'core/util/ip';
import { TestConnection } from 'app/views/settings/devices/form/TestConnection';

const ALLOWED_CREDENTIAL_TYPES = [CREDENTIAL_TYPES.snmp_v1, CREDENTIAL_TYPES.snmp_v2c, CREDENTIAL_TYPES.snmp_v3];

// Legacy SNMP collection method values (kProxy-based)
export const LEGACY_SNMP_COLLECTION_METHODS = ['direct_to_saas', 'kentik_agent,kproxy', 'kproxy'];

export const collectionMethodRenderer = ({ label, helpText, tooltip }) => (
  <Flex flexDirection="column" lineHeight="20px" mt="-1px">
    <Flex gap={1} alignItems="center">
      <Text ellipsis title={label} fontWeight="medium">
        {label}
      </Text>
      {tooltip && (
        <Tooltip content={tooltip} position="right">
          <Icon icon="info-sign" color="muted" />
        </Tooltip>
      )}
    </Flex>
    {helpText && <Text muted>{helpText}</Text>}
  </Flex>
);

@formConsumer
@inject('$auth', '$devices', '$discovery', '$credentials', '$kentikAgents', '$setup')
@observer
class IpSnmpSettings extends Component {
  // Caches for field values cleared during collection method switches, so they can be
  // restored if the user switches back to a method that requires them.
  _uaFieldCache = null;

  _kproxyFieldCache = null;

  state = {
    isAddingCredentialDialogOpen: false,
    credentialModel: null,
    isTestingConnection: false,
    testConnectionResult: null,
    hasKproxyDevices: null // Start as null, will be set by API
  };

  constructor(props) {
    super(props);
    const { form, $auth, $kentikAgents } = this.props;

    // Hide the kentik_agent option if user doesn't have recon.enabled permission
    if (!$auth.hasPermission('recon.enabled', { overrideForSudo: false })) {
      const collectionField = form.getField('snmp_collection_method');
      if (collectionField) {
        collectionField.options = collectionField.options.filter((option) => option.value !== 'kentik_agent');
        form.validate();
      }
    }

    // If the UA Flow Enrichment FF is disabled, remove the kentik_agent_flow_only option
    const rawCollectionMethod = form.getValue('snmp_collection_method');
    const collectionMethod = rawCollectionMethod ?? [];

    // Edge case: legacy devices predate the snmp_collection_method KVS field, so null means
    // kproxy is implicitly in use. Only override when null — not when explicitly set to "" (disabled)
    // or any other value (e.g. "kentik_agent"), which would have been set by modern code.
    const hasDeviceSnmpIp = !!form.getValue('device_snmp_ip');
    if (hasDeviceSnmpIp && rawCollectionMethod === null) {
      form.setValue('snmp_collection_method', ['kproxy']);
    }

    const isMonitoredByRanger =
      collectionMethod.includes('kentik_agent') || collectionMethod.includes('kentik_agent_flow_only');

    // Another edge case: if device has no collection method set, it should not have a monitoring template
    if (!isMonitoredByRanger && form.getValue('monitoring_template_id')) {
      form.setValue('monitoring_template_id', '');
    }

    // clear fields that have invalid values due to the referenced object getting deleted
    const credential_name = form.getValue('kentik_agent_snmp_options.credential_name');
    if (credential_name && !this.credentialOptions.find(({ value }) => value === credential_name)) {
      form.setValue('kentik_agent_snmp_options.credential_name', '');
    }

    const agent_id = form.getValue('kentik_agent_snmp_options.agent_id');
    if (
      agent_id &&
      !agent_id.includes?.(GROUP_ID_PREFIX) &&
      !$kentikAgents.authorizedKentikAgentOptions.find(({ value }) => value === agent_id)
    ) {
      form.setValue('kentik_agent_snmp_options.agent_id', '');
    }
  }

  componentDidMount() {
    // Check if company has any devices using kProxy to determine if kProxy options should be shown
    this.fetchHasKproxyDevices();
  }

  componentDidUpdate() {
    this.updateRules();
  }

  fetchHasKproxyDevices = async () => {
    try {
      // Suppress error toast since we fail open silently with a safe default
      const response = await api.get('/api/ui/devices/has-kproxy', { showErrorToast: false });
      this.setState({ hasKproxyDevices: response.hasKproxyDevices });
    } catch (error) {
      console.error('Failed to fetch hasKproxyDevices:', error);
      // Default to showing all options if API call fails (fail-safe)
      this.setState({ hasKproxyDevices: true });
    }
  };

  getFilteredCollectionOptions = () => {
    const { form } = this.props;
    const { hasKproxyDevices } = this.state;

    const collectionField = form.getField('snmp_collection_method');
    if (!collectionField || !collectionField.options) {
      return [];
    }

    // If we haven't loaded the kProxy status yet, return empty to show loading
    if (hasKproxyDevices === null) {
      return [];
    }

    // If company has NO kProxy devices, filter out all 3 legacy options
    if (hasKproxyDevices === false) {
      return collectionField.options.filter((option) => !LEGACY_SNMP_COLLECTION_METHODS.includes(option.value));
    }

    // Otherwise show all options
    return collectionField.options;
  };

  /**
   * Split collection options into modern and legacy groups.
   * Uses explicit value matching instead of positional slicing to avoid
   * issues when options are filtered out (e.g., kentik_agent removed when recon.enabled is false).
   */
  getModernAndLegacyOptions = (collectionMethodOptions) =>
    collectionMethodOptions.reduce(
      (acc, option) => {
        if (LEGACY_SNMP_COLLECTION_METHODS.includes(option.value)) {
          acc.legacyOptions.push(option);
        } else {
          acc.modernOptions.push(option);
        }
        return acc;
      },
      { modernOptions: [], legacyOptions: [] }
    );

  get credentialOptions() {
    const { $auth, $credentials } = this.props;
    const credentialOptions = $credentials.getCredentialOptionsByTypes(ALLOWED_CREDENTIAL_TYPES);
    if ($auth.hasRbacPermissions(['credential_vault::create'])) {
      credentialOptions.unshift({ value: '$NEW', label: 'New Credential', icon: 'plus', intent: 'primary' });
    }
    return credentialOptions;
  }

  get canTestConnection() {
    const { form } = this.props;
    const { isTestingConnection } = this.state;

    // Don't allow testing if already in progress
    if (isTestingConnection) {
      return false;
    }

    // Required fields for test connection
    const requiredFields = [
      'kentik_agent_snmp_options.credential_name',
      'kentik_agent_snmp_options.agent_id',
      'kentik_agent_snmp_options.ranger_snmp_ip'
    ];

    // Check if all required fields have values
    const hasRequiredValues = requiredFields.every((fieldName) => {
      const value = form.getValue(fieldName);
      return value && value !== '';
    });

    if (!hasRequiredValues) {
      return false;
    }

    // Disable test connection for group/cluster agent IDs; the endpoint does not resolve them
    const agent_id = form.getValue('kentik_agent_snmp_options.agent_id');
    if (agent_id && agent_id.includes(GROUP_ID_PREFIX)) {
      return false;
    }

    // Validate IP address format
    const ipAddress = form.getValue('kentik_agent_snmp_options.ranger_snmp_ip');
    if (ipAddress && !isIpValid(ipAddress.trim())) {
      return false;
    }

    // Check if form validation is passing for these fields
    const hasValidationErrors = requiredFields.some((fieldName) => {
      const field = form.getField(fieldName);
      return field && field.error;
    });

    return !hasValidationErrors;
  }

  get isAgentNotRunning() {
    const { $kentikAgents, form } = this.props;
    const agent_id = form.getValue('kentik_agent_snmp_options.agent_id');
    return (
      agent_id &&
      !agent_id.includes?.(GROUP_ID_PREFIX) &&
      $kentikAgents.collection.find({ agent_id })?.get('status') !== Statuses.UP
    );
  }

  updateRules = () => {
    const { form } = this.props;
    const renderSnmpV3 = form.getValue('device_snmp_v3_conf_enabled');
    const fieldState = form.getField('device_snmp_v3_conf.UserName');
    let rules = '';
    if (renderSnmpV3) {
      rules = 'required';
    }
    form.setRules(fieldState, rules);
  };

  handleCollectionMethodChange = (_, value, prevValue) => {
    const { form, $setup } = this.props;

    this.resetSNMPFields(prevValue, value);

    if (value.includes('kentik_agent') && !form.getValue('plan.id')) {
      // set to npak plan
      form.setValue('plan.id', $setup.plans.find(({ active, plan_type }) => active && plan_type === 'npak')?.id);
    }
  };

  resetSNMPFields = (prevCollectionMethod, newCollectionMethod) => {
    const { form } = this.props;

    // Normalize both values to arrays of individual method tokens.
    const next = Array.isArray(newCollectionMethod) ? newCollectionMethod : (newCollectionMethod?.split(',') ?? []);
    const prev = Array.isArray(prevCollectionMethod) ? prevCollectionMethod : (prevCollectionMethod?.split(',') ?? []);

    // Derive previous and next states for each group.
    const wasKentikAgent = prev.includes('kentik_agent');
    const wasFlowOnly = prev.includes('kentik_agent_flow_only');
    const wasMonitoredByUA = wasKentikAgent || wasFlowOnly;
    const wasKProxyOrDts = prev.includes('kproxy') || prev.includes('direct_to_saas');

    const isKentikAgent = next.includes('kentik_agent');
    const isFlowOnly = next.includes('kentik_agent_flow_only');
    const isMonitoredByUA = isKentikAgent || isFlowOnly;
    const isKProxy = next.includes('kproxy');
    const isDts = next.includes('direct_to_saas');
    const isKProxyOrDts = isKProxy || isDts;

    // ── monitoring_template_id ───────────────────────────────────────────────
    // Keep only when the same UA sub-type remains active to prevent an NMS template
    // lingering on a Flow-only device (or vice-versa).
    const keepTemplate = (wasKentikAgent && isKentikAgent) || (wasFlowOnly && isFlowOnly);
    if (!keepTemplate) {
      form.setValue('monitoring_template_id', null);
    }

    // ── UA / kentik_agent fields ─────────────────────────────────────────────
    if (!isMonitoredByUA) {
      // Leaving UA — snapshot current (non-empty) values for potential restoration.
      const prevCache = this._uaFieldCache ?? {};
      this._uaFieldCache = {
        credential_name: form.getValue('kentik_agent_snmp_options.credential_name') || prevCache.credential_name,
        agent_id: form.getValue('kentik_agent_snmp_options.agent_id') || prevCache.agent_id,
        ranger_snmp_ip: form.getValue('kentik_agent_snmp_options.ranger_snmp_ip') || prevCache.ranger_snmp_ip,
        port: form.getValue('kentik_agent_snmp_options.port') || prevCache.port,
        ranger_icmp_ip: form.getValue('kentik_agent_snmp_options.ranger_icmp_ip') || prevCache.ranger_icmp_ip
      };

      // Clear UA fields from form to prevent stale data from being sent to backend
      form.setValue('kentik_agent_snmp_options.credential_name', '');
      form.setValue('kentik_agent_snmp_options.agent_id', '');
      form.setValue('kentik_agent_snmp_options.ranger_snmp_ip', '');
      form.setValue('kentik_agent_snmp_options.port', '');
      form.setValue('kentik_agent_snmp_options.ranger_icmp_ip', '');
    } else if (!wasMonitoredByUA && this._uaFieldCache) {
      // Arriving at UA from a non-UA method — restore the last known good values.
      form.setValue('kentik_agent_snmp_options.credential_name', this._uaFieldCache.credential_name || '');
      form.setValue('kentik_agent_snmp_options.agent_id', this._uaFieldCache.agent_id || '');
      form.setValue('kentik_agent_snmp_options.ranger_snmp_ip', this._uaFieldCache.ranger_snmp_ip || '');
      form.setValue('kentik_agent_snmp_options.port', this._uaFieldCache.port || '');
      form.setValue('kentik_agent_snmp_options.ranger_icmp_ip', this._uaFieldCache.ranger_icmp_ip || '');
    }
    // wasMonitoredByUA && isMonitoredByUA → UA fields unchanged; user may have edited them.

    // ── kProxy / direct-to-SaaS fields ──────────────────────────────────────
    if (!isKProxyOrDts) {
      // Leaving kProxy — snapshot then clear.
      const prevCache = this._kproxyFieldCache ?? {};
      this._kproxyFieldCache = {
        minimize_snmp: form.getValue('minimize_snmp') ?? prevCache.minimize_snmp,
        device_snmp_ip: form.getValue('device_snmp_ip') || prevCache.device_snmp_ip,
        device_snmp_community: form.getValue('device_snmp_community') || prevCache.device_snmp_community,
        device_snmp_v3_conf_enabled:
          form.getValue('device_snmp_v3_conf_enabled') ?? prevCache.device_snmp_v3_conf_enabled,
        device_snmp_v3_conf_user_name:
          form.getValue('device_snmp_v3_conf.UserName') || prevCache.device_snmp_v3_conf_user_name,
        device_snmp_v3_conf_auth_protocol:
          form.getValue('device_snmp_v3_conf.AuthenticationProtocol') || prevCache.device_snmp_v3_conf_auth_protocol,
        device_snmp_v3_conf_auth_passphrase:
          form.getValue('device_snmp_v3_conf.AuthenticationPassphrase') ||
          prevCache.device_snmp_v3_conf_auth_passphrase,
        device_snmp_v3_conf_priv_protocol:
          form.getValue('device_snmp_v3_conf.PrivacyProtocol') || prevCache.device_snmp_v3_conf_priv_protocol,
        device_snmp_v3_conf_priv_passphrase:
          form.getValue('device_snmp_v3_conf.PrivacyPassphrase') || prevCache.device_snmp_v3_conf_priv_passphrase
      };
      form.setValue('minimize_snmp', false);
      form.setValue('device_snmp_ip', '');
      form.setValue('device_snmp_community', '');
      form.setValue('device_snmp_v3_conf_enabled', false);
      form.setValue('device_snmp_v3_conf.UserName', '');
      form.setValue('device_snmp_v3_conf.AuthenticationProtocol', '');
      form.setValue('device_snmp_v3_conf.AuthenticationPassphrase', '');
      form.setValue('device_snmp_v3_conf.PrivacyProtocol', '');
      form.setValue('device_snmp_v3_conf.PrivacyPassphrase', '');
    } else if (!wasKProxyOrDts && this._kproxyFieldCache) {
      // Arriving at kProxy from a non-kProxy method — restore the last known good values.
      form.setValue('minimize_snmp', this._kproxyFieldCache.minimize_snmp ?? false);
      form.setValue('device_snmp_ip', this._kproxyFieldCache.device_snmp_ip || '');
      form.setValue('device_snmp_community', this._kproxyFieldCache.device_snmp_community || '');
      form.setValue('device_snmp_v3_conf_enabled', this._kproxyFieldCache.device_snmp_v3_conf_enabled ?? false);
      form.setValue('device_snmp_v3_conf.UserName', this._kproxyFieldCache.device_snmp_v3_conf_user_name || '');
      form.setValue(
        'device_snmp_v3_conf.AuthenticationProtocol',
        this._kproxyFieldCache.device_snmp_v3_conf_auth_protocol || ''
      );
      form.setValue(
        'device_snmp_v3_conf.AuthenticationPassphrase',
        this._kproxyFieldCache.device_snmp_v3_conf_auth_passphrase || ''
      );
      form.setValue(
        'device_snmp_v3_conf.PrivacyProtocol',
        this._kproxyFieldCache.device_snmp_v3_conf_priv_protocol || ''
      );
      form.setValue(
        'device_snmp_v3_conf.PrivacyPassphrase',
        this._kproxyFieldCache.device_snmp_v3_conf_priv_passphrase || ''
      );
    }
    // wasKProxyOrDts && isKProxyOrDts → kProxy fields unchanged; user may have edited them.
  };

  handleCredentialChange = (field, value) => {
    const { $credentials } = this.props;
    // open the new credential dialog
    if (value === '$NEW') {
      field.setValue('');
      this.setState({
        isAddingCredentialDialogOpen: true,
        credentialModel: $credentials.groupedCollection.forge()
      });
    }
  };

  handleCloseCredentialsDialog = (formValues) => {
    const { form } = this.props;

    if (formValues) {
      const { credentialName } = formValues;
      form.setValue('kentik_agent_snmp_options.credential_name', credentialName);
    }

    this.setState({ isAddingCredentialDialogOpen: false, credentialModel: null });
  };

  handleTestConnection = async () => {
    const { form, $discovery } = this.props;
    this.setState({ isTestingConnection: true, testConnectionResult: null });

    const formValues = form.getValues();
    const payload = {
      agentId: formValues.kentik_agent_snmp_options.agent_id,
      credentialName: formValues.kentik_agent_snmp_options.credential_name,
      rangerSnmpIp: formValues.kentik_agent_snmp_options.ranger_snmp_ip,
      port: formValues.kentik_agent_snmp_options.port || '161'
    };

    $discovery
      .rangerTestConnection(payload)
      .then((response) => {
        const isSuccess = !!response && response.success !== false && !response.error;

        this.setState({
          testConnectionResult: {
            success: isSuccess,
            data: response,
            error: isSuccess ? null : response?.error || response?.message || 'Connection failed',
            connectionSuccessful: isSuccess,
            timestamp: Date.now(),
            parameters: payload
          }
        });
      })
      .catch((error) => {
        this.setState({
          testConnectionResult: {
            success: false,
            error: error.message || 'Request failed',
            connectionSuccessful: false,
            timestamp: Date.now(),
            parameters: payload,
            showParameters: false
          }
        });
      })
      .finally(() => {
        this.setState({ isTestingConnection: false });
      });
  };

  render() {
    const { $auth, $devices, $kentikAgents, form, role } = this.props;
    const { isAddingCredentialDialogOpen, credentialModel, isTestingConnection, testConnectionResult } = this.state;
    const { model } = form;
    const { device_snmp_v3_conf_enabled, device_subtype, snmp_collection_method, device_snmp_v3_conf } =
      form.getValues();
    // Derive device and collection state used to control the SNMP settings UI.

    const deviceType = $devices.getDeviceTypeFromSubtype(device_subtype);
    const iskProxyCollectionEnabled = snmp_collection_method?.includes('kproxy');
    const isDirectToSaasEnabled = snmp_collection_method?.includes('direct_to_saas');
    const isFlowCollectionViaUAEnabled = snmp_collection_method?.includes('kentik_agent_flow_only');
    const isKentikAgentCollectionEnabled = snmp_collection_method?.includes('kentik_agent');
    const isDoublePollingEnabled = iskProxyCollectionEnabled && isKentikAgentCollectionEnabled;

    const ariaProps = {};
    if (role) {
      ariaProps.role = role;
    }

    // Get filtered options based on kProxy device status
    const collectionMethodOptions = this.getFilteredCollectionOptions();
    const { modernOptions, legacyOptions } = this.getModernAndLegacyOptions(collectionMethodOptions);
    const { hasKproxyDevices } = this.state;
    const isLoadingOptions = hasKproxyDevices === null;

    return (
      <Flex flexDirection="column" gap={2} overflow="auto" p={2} {...ariaProps}>
        <Heading level={5} pb="6px" mb={0} borderBottom="thin">
          Collection Method
        </Heading>

        {/* Show loading state while fetching kProxy status */}
        {isLoadingOptions && (
          <Flex alignItems="center" gap={2} p={2}>
            <Text color="muted">Loading collection methods...</Text>
          </Flex>
        )}

        {/* Feature flag enabled: Use new RadioGroup workflow with collection types */}
        {!isLoadingOptions && (
          <Flex gap={4}>
            {/* Left side: Radio buttons */}
            <Flex flexDirection="column" flex="1 1 auto" maxWidth="550px" pr={4}>
              <Field
                onChange={this.handleCollectionMethodChange}
                name="snmp_collection_method"
                showLabel={false}
                m={0}
                validateOptions={false}
              >
                {(fieldProps) => (
                  <Flex flexDirection="column" gap={0}>
                    <RadioGroup
                      {...fieldProps}
                      containerProps={{ flexDirection: 'column', gap: '8px' }}
                      itemRenderer={collectionMethodRenderer}
                      options={modernOptions}
                    />

                    {legacyOptions.length > 0 && (
                      <>
                        <Text fontWeight="bold" mt="16px" mb="8px">
                          Legacy methods - Requiring migration to one of the above methods.
                        </Text>

                        <RadioGroup
                          {...fieldProps}
                          containerProps={{ flexDirection: 'column', gap: '8px' }}
                          itemRenderer={collectionMethodRenderer}
                          options={legacyOptions}
                        />
                      </>
                    )}
                  </Flex>
                )}
              </Field>
            </Flex>

            {/* Right side: Configuration fields based on selected option */}
            <Flex flexDirection="column" flex="1" minWidth="350px" maxWidth="450px" gap={2}>
              {isDoublePollingEnabled && (
                <Heading level={5} pb="6px" mb={0} borderBottom="thin">
                  Full Monitoring via UA
                </Heading>
              )}
              {isKentikAgentCollectionEnabled && (
                <>
                  {this.isAgentNotRunning && (
                    <Callout intent="danger">
                      <Flex gap={1} alignItems="center">
                        <Icon icon="error" color="danger" />
                        <Text>This agent is not currently running, and will not be able to receive updates.</Text>
                      </Flex>
                    </Callout>
                  )}

                  <Flex gap={2}>
                    <Field m={0} name="kentik_agent_snmp_options.ranger_snmp_ip" flex={1}>
                      <InputGroup fill />
                    </Field>

                    <Field m={0} name="kentik_agent_snmp_options.port" placeholder="161" flex="0 0 120px">
                      <InputGroup fill />
                    </Field>
                  </Flex>

                  <Field
                    m={0}
                    name="kentik_agent_snmp_options.agent_id"
                    options={$kentikAgents.nmsAssignmentOptions}
                    disabled={false}
                  >
                    <Select fill showFilter optionRenderer={KentikAgentOptionRenderer} />
                  </Field>

                  <Field
                    disabled={!$auth.hasRbacPermissions(['credential_vault::read'])}
                    m={0}
                    onChange={this.handleCredentialChange}
                    name="kentik_agent_snmp_options.credential_name"
                    options={this.credentialOptions}
                  >
                    <Select
                      fill
                      optionRenderer={CredentialOptionRenderer}
                      valueRenderer={CredentialValueRenderer}
                      showFilter
                    />
                  </Field>

                  <Field m={0} name="monitoring_template_id">
                    <MonitoringTemplateSelector fill />
                  </Field>

                  <TestConnection
                    canTestConnection={this.canTestConnection}
                    handleTestConnection={this.handleTestConnection}
                    isTestingConnection={isTestingConnection}
                    testConnectionResult={testConnectionResult}
                  />
                </>
              )}

              {isFlowCollectionViaUAEnabled && (
                <>
                  {this.isAgentNotRunning && (
                    <Callout intent="danger">
                      <Flex gap={1} alignItems="center">
                        <Icon icon="error" color="danger" />
                        <Text>This agent is not currently running, and will not be able to receive updates.</Text>
                      </Flex>
                    </Callout>
                  )}

                  <Flex gap={2}>
                    <Field m={0} name="kentik_agent_snmp_options.ranger_snmp_ip" flex={1}>
                      <InputGroup fill />
                    </Field>

                    <Field m={0} name="kentik_agent_snmp_options.port" placeholder="161" flex="0 0 120px">
                      <InputGroup fill />
                    </Field>
                  </Flex>

                  <Field
                    m={0}
                    name="kentik_agent_snmp_options.agent_id"
                    options={$kentikAgents.authorizedKentikAgentOptions}
                    disabled={false}
                  >
                    <Select fill optionRenderer={KentikAgentOptionRenderer} showFilter />
                  </Field>

                  <Field
                    disabled={!$auth.hasRbacPermissions(['credential_vault::read'])}
                    m={0}
                    onChange={this.handleCredentialChange}
                    name="kentik_agent_snmp_options.credential_name"
                    options={this.credentialOptions}
                  >
                    <Select
                      fill
                      optionRenderer={CredentialOptionRenderer}
                      valueRenderer={CredentialValueRenderer}
                      showFilter
                    />
                  </Field>

                  <Field m={0} name="monitoring_template_id">
                    <MonitoringTemplateSelector templateType="FLOW" fill />
                  </Field>

                  <TestConnection
                    canTestConnection={this.canTestConnection}
                    handleTestConnection={this.handleTestConnection}
                    isTestingConnection={isTestingConnection}
                    testConnectionResult={testConnectionResult}
                  />
                </>
              )}

              {(iskProxyCollectionEnabled || isDirectToSaasEnabled) && (
                <>
                  {isDoublePollingEnabled && (
                    <Heading level={5} pb="6px" mb={0} borderBottom="thin">
                      kproxy for Flow Enrichment
                    </Heading>
                  )}
                  <Callout intent="warning" p={2}>
                    <Flex gap={1} alignItems="center" justifyContent="space-between">
                      <Icon mr="12px" icon="warning-sign" color="warning" iconSize={40} mt="2px" />
                      <Flex flexDirection="column" flex={1}>
                        <Heading level={6} m={0}>
                          This legacy flow enrichment method has limitations
                        </Heading>
                        <Text>
                          In this legacy mode, using SNMP credentials from the Credentials Vault is unavailable, as well
                          as multiple SNMPv3 Auth/Priv methods.
                        </Text>
                      </Flex>
                    </Flex>
                  </Callout>

                  <Field m={0} name="device_snmp_ip">
                    <InputGroup fill />
                  </Field>

                  <Field m={0} name="minimize_snmp" rules={deviceType === 'router' ? 'required' : []}>
                    <Select fill />
                  </Field>

                  <Field m={0} name="device_snmp_v3_conf_enabled" showLabel={false} onChange={this.updateRules}>
                    {(fieldProps) => {
                      const { value: fieldValue, field, ...restFieldProps } = fieldProps;
                      const selectedValue = fieldValue ? 'v3' : 'v2c';

                      return (
                        <RadioGroup
                          {...restFieldProps}
                          inline
                          large
                          selectedValue={selectedValue}
                          value={selectedValue}
                          onChange={(value) => {
                            const boolValue = value === 'v3';
                            form.setValue('device_snmp_v3_conf_enabled', boolValue);
                            this.updateRules();
                          }}
                          options={[
                            { value: 'v2c', label: 'v2c and below' },
                            { value: 'v3', label: 'v3' }
                          ]}
                        />
                      );
                    }}
                  </Field>

                  {!device_snmp_v3_conf_enabled && (
                    <Field m={0} name="device_snmp_community">
                      <PasswordInput fill autoComplete="new-password" />
                    </Field>
                  )}

                  {device_snmp_v3_conf_enabled && (
                    <Flex flexDirection="column" gap={2}>
                      <Field m={0} name="device_snmp_v3_conf.UserName">
                        <InputGroup width={400} />
                      </Field>

                      <Grid>
                        <FormGroup label="SNMPv3 Authentication" fontSize="small" mb={0}>
                          <ControlGroup>
                            <Field
                              large
                              name="device_snmp_v3_conf.AuthenticationProtocol"
                              showLabel={false}
                              onChange={this.updateRules}
                            >
                              <Select menuWidth={100} />
                            </Field>
                            {!(device_snmp_v3_conf.AuthenticationProtocol === 'NoAuth') && (
                              <Field
                                large
                                name="device_snmp_v3_conf.AuthenticationPassphrase"
                                showLabel={false}
                                placeholder="Passphrase"
                                flex={1}
                              >
                                <PasswordInput leftIcon="lock" />
                              </Field>
                            )}
                          </ControlGroup>
                        </FormGroup>

                        <FormGroup label="SNMPv3 Privacy" mb={0}>
                          <ControlGroup>
                            <Field
                              large
                              name="device_snmp_v3_conf.PrivacyProtocol"
                              showLabel={false}
                              onChange={this.updateRules}
                            >
                              <Select menuWidth={100} />
                            </Field>
                            {!(device_snmp_v3_conf.PrivacyProtocol === 'NoPriv') && (
                              <Field
                                large
                                name="device_snmp_v3_conf.PrivacyPassphrase"
                                showLabel={false}
                                placeholder="Passphrase"
                                flex={1}
                              >
                                <PasswordInput leftIcon="lock" />
                              </Field>
                            )}
                          </ControlGroup>
                        </FormGroup>
                      </Grid>
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          </Flex>
        )}

        {/* Legacy path: Feature flag disabled - uses CheckboxGroup */}
        {/* Note: Filtering is not applied here as this path will be removed when FF is made permanent */}

        {model.isICMP && (
          <Flex flexDirection="column" gap={2}>
            <Heading level={5} pb="6px" mb={0} borderBottom="thin">
              ICMP
            </Heading>
            <Flex gap={2}>
              <Flex flexDirection="column" gap={1} flex={1}>
                <Field name="kentik_agent_snmp_options.ranger_icmp_ip">
                  <InputGroup />
                </Field>
              </Flex>

              <Flex flexDirection="column" gap={1} flex={1}>
                <Field
                  m={0}
                  flex={1}
                  name="kentik_agent_snmp_options.agent_id"
                  options={$kentikAgents.authorizedKentikAgentOptions}
                  disabled={false}
                >
                  <Select fill optionRenderer={KentikAgentOptionRenderer} />
                </Field>
              </Flex>
            </Flex>
          </Flex>
        )}
        {credentialModel && (
          <SetupCredentials
            isOpen={isAddingCredentialDialogOpen}
            model={credentialModel}
            onClose={this.handleCloseCredentialsDialog}
            // only SNMP credential types are valid here
            types={ALLOWED_CREDENTIAL_TYPES}
            title="Add SNMP Credential"
          />
        )}
      </Flex>
    );
  }
}

export default IpSnmpSettings;
